import { useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, MODELS } from "@/lib/api";
import { PanelTester } from "@/components/PanelTester";

const MODES = [
  { v: "panel_first", t: "Painel primeiro + IA", d: "Envia ao painel; se ele não responder, a IA responde." },
  { v: "ai_only", t: "Só IA", d: "Todas as mensagens são respondidas pela IA." },
  { v: "panel_only", t: "Só Painel", d: "Funciona como antes, apenas repassa ao painel." },
];

const Field = ({ label, hint, children }) => (
  <div className="space-y-2">
    <Label className="text-sm font-medium">{label}</Label>
    {children}
    {hint && <p className="text-xs text-[#6B7280]">{hint}</p>}
  </div>
);

export const SettingsForm = ({ settings, onSaved }) => {
  const [f, setF] = useState(settings);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setCmd = (i, k, v) => set("panel_commands", f.panel_commands.map((c, j) => (j === i ? { ...c, [k]: v } : c)));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings", { ...f, panel_commands: f.panel_commands.filter((c) => c.keyword.trim()) });
      onSaved(r.data); setF(r.data);
      toast.success("Configurações salvas");
    } catch { toast.error("Erro ao salvar"); }
    setSaving(false);
  };

  return (
    <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6" data-testid="settings-form">
      <section className="panel-card space-y-6">
        <Field label="URL do chatbot do painel IPTV" hint="A URL que o painel mandou colar no Auto Reply (com o ?k=...).">
          <Input value={f.panel_url} onChange={(e) => set("panel_url", e.target.value)} placeholder="https://painel.digitalplus.top/chatbot/check/?k=..." className="input-dark font-mono text-xs" data-testid="panel-url-input" />
        </Field>
        <Field label="Modo de funcionamento">
          <RadioGroup value={f.mode} onValueChange={(v) => set("mode", v)} className="space-y-2" data-testid="mode-radio-group">
            {MODES.map((m) => (
              <label key={m.v} className={`mode-option ${f.mode === m.v ? "active" : ""}`} data-testid={`mode-${m.v}`}>
                <RadioGroupItem value={m.v} className="mt-1" />
                <div><p className="text-sm font-medium">{m.t}</p><p className="text-xs text-[#9CA3AF]">{m.d}</p></div>
              </label>
            ))}
          </RadioGroup>
        </Field>
        <div className="flex items-center justify-between gap-4 mode-option">
          <div><p className="text-sm font-medium">IA ativada</p><p className="text-xs text-[#9CA3AF]">Desligue para pausar a IA sem mudar o modo.</p></div>
          <Switch checked={f.ai_enabled} onCheckedChange={(v) => set("ai_enabled", v)} data-testid="ai-enabled-switch" />
        </div>
        <div className="flex items-center justify-between gap-4 mode-option">
          <div><p className="text-sm font-medium">Vendas pela IA (modo teste)</p><p className="text-xs text-[#9CA3AF]">A IA cria pedidos com a tabela oficial; PIX e ativação ficam em ambiente de teste.</p></div>
          <Switch checked={f.sales_enabled} onCheckedChange={(v) => set("sales_enabled", v)} data-testid="sales-enabled-switch" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nome do bot">
            <Input value={f.bot_name} onChange={(e) => set("bot_name", e.target.value)} className="input-dark" data-testid="bot-name-input" />
          </Field>
          <Field label="Modelo de IA">
            <Select value={`${f.provider}|${f.model}`} onValueChange={(v) => { const [p, m] = v.split("|"); setF((o) => ({ ...o, provider: p, model: m })); }}>
              <SelectTrigger className="input-dark" data-testid="model-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#1F2937] text-[#F9FAFB]">
                {MODELS.map((m) => <SelectItem key={m.value} value={m.value} data-testid={`model-option-${m.value}`}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </section>

      <section className="panel-card space-y-6">
        <Field label="Instruções e base de conhecimento da IA" hint="Planos, preços, PIX, apps, regras de atendimento. A IA só responde com base nisso.">
          <Textarea value={f.system_prompt} onChange={(e) => set("system_prompt", e.target.value)} rows={14} className="input-dark text-sm leading-relaxed" data-testid="system-prompt-textarea" />
        </Field>
        <Field label="Comandos do painel acionados pela IA" hint="Ex.: cliente diz 'quero testar' → a IA envia a palavra 'teste' ao painel e devolve a resposta dele (login do teste).">
          <div className="space-y-2">
            {f.panel_commands.map((c, i) => (
              <div key={i} className="flex gap-2" data-testid={`command-row-${i}`}>
                <Input value={c.keyword} onChange={(e) => setCmd(i, "keyword", e.target.value)} placeholder="palavra" className="input-dark font-mono w-28 sm:w-36" data-testid={`command-keyword-${i}`} />
                <Input value={c.description} onChange={(e) => setCmd(i, "description", e.target.value)} placeholder="quando usar" className="input-dark flex-1" data-testid={`command-description-${i}`} />
                <Button variant="ghost" size="icon" className="h-10 w-10 text-[#9CA3AF] hover:text-red-400" onClick={() => set("panel_commands", f.panel_commands.filter((_, j) => j !== i))} data-testid={`command-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button variant="outline" className="btn-ghost h-10" onClick={() => set("panel_commands", [...f.panel_commands, { keyword: "", description: "" }])} data-testid="command-add-button"><Plus className="w-4 h-4 mr-1" />Adicionar comando</Button>
          </div>
        </Field>
        <Button onClick={save} disabled={saving} className="btn-primary h-12 w-full" data-testid="save-settings-button"><Save className="w-4 h-4 mr-2" />{saving ? "Salvando..." : "Salvar configurações"}</Button>
      </section>
      <PanelTester />
    </div>
  );
};
